/**
 * Kai filter vocabulary — the phrase → filter mapping (EN + ES).
 *
 * This is pure data, deliberately kept out of i18n.js: i18n.js is UI copy that
 * translators may reword, whereas these strings are a parser grammar. Rewording
 * "overdue" here changes behaviour, not wording.
 *
 * Every value on the right-hand side must be one Nautilus actually supports.
 * Where a common phrase has no equivalent (assignee, effort, sort, group by),
 * it is listed in UNSUPPORTED_TERMS so Kai can say so instead of guessing.
 *
 * @module kaiFilterVocabulary
 */

/**
 * "Open work" is NOT an enumeration of todo+progress+review.
 *
 * The app's own dashboard shortcut (dashOpenTasks, app.js) defines open work as
 * "everything except done" — and that is the only definition that stays correct
 * when `backlog` exists and when the `review` status is disabled in settings.
 */
export const STATUS_GROUP_ALIASES = {
    // → not done
    'open': { statuses: ['done'], excludeMode: true },
    'still open': { statuses: ['done'], excludeMode: true },
    'incomplete': { statuses: ['done'], excludeMode: true },
    'unfinished': { statuses: ['done'], excludeMode: true },
    'not done': { statuses: ['done'], excludeMode: true },
    'not completed': { statuses: ['done'], excludeMode: true },
    'outstanding': { statuses: ['done'], excludeMode: true },
    'pending': { statuses: ['done'], excludeMode: true },
    'active': { statuses: ['done'], excludeMode: true },
    'abierta': { statuses: ['done'], excludeMode: true },
    'abiertas': { statuses: ['done'], excludeMode: true },
    'pendiente': { statuses: ['done'], excludeMode: true },
    'pendientes': { statuses: ['done'], excludeMode: true },
    'sin terminar': { statuses: ['done'], excludeMode: true },
    'sin completar': { statuses: ['done'], excludeMode: true },
    'incompletas': { statuses: ['done'], excludeMode: true },
    // → done
    'closed': { statuses: ['done'], excludeMode: false },
    'complete': { statuses: ['done'], excludeMode: false },
    'completed': { statuses: ['done'], excludeMode: false },
    'finished': { statuses: ['done'], excludeMode: false },
    'completada': { statuses: ['done'], excludeMode: false },
    'completadas': { statuses: ['done'], excludeMode: false },
    'terminadas': { statuses: ['done'], excludeMode: false },
    'cerradas': { statuses: ['done'], excludeMode: false }
};

/** Exact status names. Nautilus has five and no more. */
export const STATUS_ALIASES = {
    'backlog': 'backlog',
    'icebox': 'backlog',
    'someday': 'backlog',
    'to do': 'todo',
    'todo': 'todo',
    'to-do': 'todo',
    'not started': 'todo',
    'waiting to be started': 'todo',
    'por hacer': 'todo',
    'in progress': 'progress',
    'progress': 'progress',
    'in-progress': 'progress',
    'doing': 'progress',
    'wip': 'progress',
    'being worked on': 'progress',
    'en curso': 'progress',
    'en progreso': 'progress',
    'in review': 'review',
    'review': 'review',
    'under review': 'review',
    'waiting for review': 'review',
    'awaiting review': 'review',
    'en revision': 'review',
    'done': 'done',
    'hecha': 'done',
    'hechas': 'done',
    'terminada': 'done'
};

/**
 * Nautilus priorities are low | medium | high. "Urgent", "critical" and
 * "highest" are all the same thing here — there is no fourth level.
 */
export const PRIORITY_ALIASES = {
    'high': 'high',
    'highest': 'high',
    'urgent': 'high',
    'critical': 'high',
    'important': 'high',
    'top priority': 'high',
    'asap': 'high',
    'p0': 'high',
    'p1': 'high',
    'alta': 'high',
    'urgente': 'high',
    'urgentes': 'high',
    'critica': 'high',
    'importante': 'high',
    'importantes': 'high',
    'medium': 'medium',
    'normal': 'medium',
    'moderate': 'medium',
    'media': 'medium',
    'normales': 'medium',
    'low': 'low',
    'lowest': 'low',
    'minor': 'low',
    'trivial': 'low',
    'nice to have': 'low',
    'baja': 'low',
    'menor': 'low',
    'bajas': 'low'
};

/**
 * "Blocked" is a LINK, not a status. Nautilus models blocking with
 * task.links[].type and the dependencies map — filterState.linkTypes already
 * exposes exactly this. Mapping it to a status would invent data.
 */
export const LINK_TYPE_ALIASES = {
    'blocked': 'is_blocked_by',
    'blocked by': 'is_blocked_by',
    'is blocked': 'is_blocked_by',
    'stuck': 'is_blocked_by',
    'waiting on': 'is_blocked_by',
    'cannot continue': 'is_blocked_by',
    'bloqueada': 'is_blocked_by',
    'bloqueadas': 'is_blocked_by',
    'blocking': 'blocks',
    'blocks others': 'blocks',
    'bloquea': 'blocks',
    'related': 'relates_to',
    'related to': 'relates_to',
    'relacionada': 'relates_to',
    'has links': 'has_web_links',
    'with links': 'has_web_links',
    'con enlaces': 'has_web_links',
    'no links': 'no_links',
    'unlinked': 'no_links',
    'sin enlaces': 'no_links'
};

/** The 14 presets implemented by matchesDatePreset() in filterPredicates.js. */
export const DATE_FIELD_CUES = {
    endDate: ['con fecha de vencimiento', 'fecha de vencimiento', 'con fecha de entrega', 'fecha de entrega', 'con fecha de fin', 'fecha de fin', 'con fecha limite', 'fecha limite', 'con vencimiento', 'vencimiento', 'vencen', 'vence', 'venceran', 'vencer', 'finalizan', 'finaliza', 'terminan', 'termina', 'with a due date', 'with an end date', 'with due date', 'due date', 'end date', 'deadline', 'due', 'ending', 'ends', 'end'],
    startDate: ['con fecha de inicio', 'fecha de inicio', 'empiezan', 'empieza', 'comienzan', 'comienza', 'inician', 'inicia', 'with a start date', 'start date', 'starting', 'starts', 'start', 'begins', 'begin']
};

export const DATE_PRESET_ALIASES = {
    'overdue': 'overdue',
    'late': 'overdue',
    'past due': 'overdue',
    'vencida': 'overdue',
    'vencidas': 'overdue',
    'atrasadas': 'overdue',
    'due today': 'end-today',
    'today': 'end-today',
    'hoy': 'end-today',
    'due tomorrow': 'end-tomorrow',
    'tomorrow': 'end-tomorrow',
    'manana': 'end-tomorrow',
    'due this week': 'end-week',
    'this week': 'end-week',
    'esta semana': 'end-week',
    'next 7 days': 'end-week',
    'due this month': 'end-month',
    'this month': 'end-month',
    'este mes': 'end-month',
    'next 30 days': 'end-month',
    'no due date': 'no-date',
    'no end date': 'no-date',
    'without a due date': 'no-date',
    'without due date': 'no-date',
    'without an end date': 'no-date',
    'without end date': 'no-date',
    'with no due date': 'no-date',
    'with no end date': 'no-date',
    'no deadline': 'no-date',
    'undated': 'no-date',
    'sin fecha': 'no-date',
    'sin fecha de fin': 'no-date',
    // Argentine users routinely mix the English "due date" into a Spanish sentence,
    // and say "vencimiento" for what Nautilus calls the end date.
    'sin due date': 'no-date',
    'sin fecha de vencimiento': 'no-date',
    'sin vencimiento': 'no-date',
    'sin fecha limite': 'no-date',
    'sin fecha de entrega': 'no-date',
    // The positive counterpart. Nautilus calls this the End Date; users say "due date".
    'with an end date': 'has-date',
    'with end date': 'has-date',
    'with a due date': 'has-date',
    'with due date': 'has-date',
    'has an end date': 'has-date',
    'has a due date': 'has-date',
    'have an end date': 'has-date',
    'have a due date': 'has-date',
    'that have an end date': 'has-date',
    'with a deadline': 'has-date',
    'con fecha de fin': 'has-date',
    'con fecha': 'has-date',
    'con due date': 'has-date',
    'con fecha de vencimiento': 'has-date',
    'con vencimiento': 'has-date',
    'no start date': 'no-start-date',
    'sin fecha de inicio': 'no-start-date',
    'with a start date': 'has-start-date',
    'with start date': 'has-start-date',
    'has a start date': 'has-start-date',
    'con fecha de inicio': 'has-start-date',
    'already started': 'already-started',
    'en marcha': 'already-started',
    'starting today': 'start-today',
    'starting tomorrow': 'start-tomorrow',
    'starting this week': 'start-week',
    'starting this month': 'start-month'
};

/**
 * Which timestamp a period applies to. "created last week", "modified in March",
 * "completed this month". The completion verbs overlap with the `done` status
 * ("closed"/"finished"), so an activity range is only taken when a period phrase
 * actually follows — see applyActivityRanges in the parser.
 */
export const ACTIVITY_VERBS = {
    created: ['created', 'added', 'made', 'opened', 'creada', 'creadas', 'creado', 'creados', 'anadida', 'anadidas', 'agregada', 'agregadas'],
    updated: ['updated', 'modified', 'changed', 'edited', 'touched', 'actualizada', 'actualizadas', 'modificada', 'modificadas', 'cambiada', 'cambiadas', 'editada', 'editadas'],
    completed: ['completed', 'closed', 'resolved', 'finished', 'completada', 'completadas', 'cerrada', 'cerradas', 'resuelta', 'resueltas', 'terminada', 'terminadas', 'finalizada', 'finalizadas']
};

/** Project status is derived (getProjectStatus in app.js), never stored. */
export const PROJECT_STATUS_ALIASES = {
    'planning': 'planning',
    'planificacion': 'planning',
    'active': 'active',
    'activo': 'active',
    'activos': 'active',
    'en curso': 'active',
    'completed': 'completed',
    'complete': 'completed',
    'finished': 'completed',
    'closed': 'completed',
    'completado': 'completed',
    'completados': 'completed',
    'completada': 'completed',
    'completadas': 'completed',
    // "closed" project == completed. These are project-scoped only (the dictionary
    // is entity-gated), so they never touch the task-level 'closed' → done mapping.
    'cerrado': 'completed',
    'cerrados': 'completed',
    'cerrada': 'completed',
    'cerradas': 'completed',
    'terminado': 'completed',
    'terminados': 'completed',
    'finalizado': 'completed',
    'finalizados': 'completed',
    'backlog': 'backlog'
};

/** Project-level "open" mirrors dashOpenProjects: everything except completed. */
export const PROJECT_STATUS_GROUP_ALIASES = {
    'open': { statuses: ['completed'], excludeMode: true },
    'still open': { statuses: ['completed'], excludeMode: true },
    'unfinished': { statuses: ['completed'], excludeMode: true },
    'incomplete': { statuses: ['completed'], excludeMode: true },
    'not closed': { statuses: ['completed'], excludeMode: true },
    'not finished': { statuses: ['completed'], excludeMode: true },
    'abiertos': { statuses: ['completed'], excludeMode: true },
    'abiertas': { statuses: ['completed'], excludeMode: true },
    'sin terminar': { statuses: ['completed'], excludeMode: true },
    'sin cerrar': { statuses: ['completed'], excludeMode: true }
};

/**
 * "Recently updated" IS supported — filterState carries an updated-window filter
 * (window.kanbanUpdatedFilter for tasks, projectFilterState.updatedFilter for
 * projects). Only *sorting* by update time is impossible.
 */
export const UPDATED_FILTER_ALIASES = {
    'updated today': '24h',
    'modified today': '24h',
    'changed today': '24h',
    'recently updated': '24h',
    'updated recently': '24h',
    'recently modified': '24h',
    'recently changed': '24h',
    'in the last 24 hours': '24h',
    'in the last day': '24h',
    'actualizadas hoy': '24h',
    'modificadas hoy': '24h',
    'updated this week': 'week',
    'modified this week': 'week',
    'changed this week': 'week',
    'in the last week': 'week',
    'actualizadas esta semana': 'week',
    'modificadas esta semana': 'week',
    'updated this month': 'month',
    'modified this month': 'month',
    'changed this month': 'month',
    'in the last month': 'month',
    'actualizadas este mes': 'month',
    'modificadas este mes': 'month',
    'in the last 5 minutes': '5m',
    'in the last 30 minutes': '30m'
};

/**
 * "in the last N days/hours/minutes".
 *
 * Nautilus's updated filter has FIXED windows — 5m, 30m, 24h, this week, this
 * month. There is no arbitrary N. Rounding "the past 4 days" up to "this week"
 * would quietly hand back 7 days of tasks, so only the windows that map exactly
 * are accepted; anything else is reported as unsupported, with the real options.
 */
export const UPDATED_WINDOW_PATTERN =
    /\b(?:in|over|during|within|from)?\s*(?:the\s+)?(?:past|last)\s+(\d+)\s*(minutes?|mins?|hours?|hrs?|days?|weeks?|months?)\b/i;

/** Exact window → filter value. Anything absent here is honestly unsupported. */
export const UPDATED_WINDOW_EXACT = {
    'minute:5': '5m',
    'minute:30': '30m',
    'hour:24': '24h',
    'day:1': '24h',
    'day:7': 'week',
    'week:1': 'week',
    'day:30': 'month',
    'day:31': 'month',
    'month:1': 'month'
};

/** The windows a user can actually pick, for the "I can't do that, but I can do…" message. */
export const UPDATED_WINDOW_OPTIONS = '5 minutes, 30 minutes, 24 hours, this week, this month';

/**
 * Does this read like a question rather than an instruction to create something?
 *
 * Auto-detect is barred from resolving to query_filter on the server (so no existing
 * create request can drift into it), which means the CLIENT has to spot a query and
 * route it. Anchored at the start of the sentence: "show me open tasks" is a query,
 * while "create a list of tasks" is not — and starting with a create verb always wins.
 */
export const QUERY_INTENT_PATTERN =
    new RegExp(
        '^(?:kai[,!\\s]+)?(?:hey[,\\s]+)?' +
        // Optional modal + pronoun in either order: "can you", "me podes", "podrias",
        // "me puedes", "could you please"...
        '(?:(?:me\\s+)?(?:can|could|would|do|are|is|please|podrias|podras|puedes|podes)\\s*)?' +
        '(?:you\\s+|i\\s+|there\\s+|me\\s+|te\\s+)?' +
        '(?:please\\s+|help\\s+me\\s+)?' +
        '(?:' +
        // English
        'show|find|search|list|display|filter|give\\s+me|get\\s+me|fetch|look\\s+up|' +
        'tell\\s+me|show\\s+me|are\\s+there|is\\s+there|do\\s+i\\s+have|how\\s+many|how\\s+much|count|' +
        'i\\s+need|i\\s+want|i\\s+would\\s+like|what|which|who|whose|' +
        // Spanish (incl. Argentine voseo: mostrame/decime/contame, and infinitives
        // after a modal: "me podes MOSTRAR", "podrias MOSTRARME")
        'muestra|muestrame|mostrame|mostrar|mostrarme|ensename|ensenar|ensename|ver|' +
        'dime|decime|decirme|dame|busca|buscame|buscar|encuentra|encontrar|encontrame|lista|listar|listame|' +
        'filtra|filtrar|cuenta|contar|contame|cuentame|cuales|cuantas|cuantos|' +
        // More voseo imperatives: "fijate si hay…", "traeme…", "pasame…", "mandame…"
        'fijate|traeme|traer|pasame|mandame|' +
        'cuanto|hay|tengo|que\\s+tarea|que\\s+proyecto' +
        ')\\b',
        'i'
    );

/**
 * Project completion thresholds: "more than 75% of their tasks done".
 *
 * Read from the RAW text, not the normalized token stream — normalizeText() strips
 * the "%" sign, which is the only thing distinguishing a percentage from any other
 * number in the sentence.
 *
 * Comparators are taken literally: "more than 75%" excludes a project sitting at
 * exactly 75%, while "at least 75%" includes it. Guessing which one the user meant
 * would be the kind of silent approximation this whole feature exists to avoid.
 */
export const COMPLETION_PATTERN =
    /\b(more than|greater than|over|above|at least|no less than|minimum(?: of)?|less than|under|below|fewer than|at most|no more than|maximum(?: of)?|exactly|precisely|between)\s+(\d{1,3})\s*(?:%|percent|per cent)(?:\s*(?:and|to|-)\s*(\d{1,3})\s*(?:%|percent|per cent)?)?/i;

/** Whole-phrase completion states that need no number. */
export const COMPLETION_PHRASES = {
    'fully complete': { min: 100, max: 100 },
    'fully completed': { min: 100, max: 100 },
    '100% complete': { min: 100, max: 100 },
    '100% done': { min: 100, max: 100 },
    'all tasks done': { min: 100, max: 100 },
    'all their tasks done': { min: 100, max: 100 },
    'completamente terminados': { min: 100, max: 100 },
    'not started': { min: 0, max: 0 },
    'nothing done': { min: 0, max: 0 },
    'no tasks done': { min: 0, max: 0 },
    '0% done': { min: 0, max: 0 },
    'sin empezar': { min: 0, max: 0 }
};

/**
 * A create verb beats the query heuristic — the VERB decides, not the modal.
 * "puedes crearme una tarea" / "me podes crear 3 tareas" are creation, even though
 * they open with the same "can you" as a query. Checked first in
 * isKaiQueryLikeInstruction, so if this matches the request is never a query.
 */
export const CREATE_INTENT_PATTERN =
    new RegExp(
        '^(?:kai[,!\\s]+)?(?:hey[,\\s]+)?' +
        '(?:(?:me\\s+)?(?:can|could|would|please|podrias|podras|puedes|podes)\\s*)?' +
        '(?:you\\s+|me\\s+)?(?:please\\s+)?' +
        '(?:' +
        'create|add|make|new|draft|write|generate|plan|set\\s+up|build|schedule|automate|rewrite|summari[sz]e|' +
        // Spanish, incl. infinitive + clitic ("crearme", "crearnos", "agregarme")
        'crea|crear|crea?rme|crearnos|anade|anadir|agrega|agregar|agregarme|genera|generar|' +
        'redacta|redactar|planifica|planificar|resume|resumir|reescribe|reescribir|arma|armar' +
        ')\\b',
        'i'
    );

/** projectFilterState.taskFilter — the only project↔task relation we can express. */
export const PROJECT_TASK_FILTER_ALIASES = {
    'with tasks': 'has-tasks',
    'with at least one task': 'has-tasks',
    'con tareas': 'has-tasks',
    'without tasks': 'no-tasks',
    'with no tasks': 'no-tasks',
    'no tasks': 'no-tasks',
    'empty': 'no-tasks',
    'sin tareas': 'no-tasks'
};

export const ENTITY_ALIASES = {
    'task': 'tasks', 'tasks': 'tasks', 'tarea': 'tasks', 'tareas': 'tasks',
    'project': 'projects', 'projects': 'projects', 'proyecto': 'projects', 'proyectos': 'projects'
};

export const SCOPE_ALIASES = {
    'archived': 'archived', 'archive': 'archived',
    'archivada': 'archived', 'archivadas': 'archived', 'archivados': 'archived'
};

export const VIEW_ALIASES = {
    'kanban': 'kanban', 'board': 'kanban', 'tablero': 'kanban',
    'list': 'list', 'lista': 'list', 'table': 'list',
    'calendar': 'calendar', 'calendario': 'calendar'
};

/** "no tags" / "no project" — the "none" sentinels understood by filterPredicates. */
export const NONE_TAG_PHRASES = [
    'no assignee', 'unassigned', 'assigned to nobody', 'assigned to no one', 'nobody',
    'without an owner', 'with no owner', 'no owner', 'do not have an owner', 'dont have an owner',
    'untagged', 'no tags', 'no tag', 'without tags', 'without any tags', 'with no tags',
    'do not have tags', 'dont have tags', 'do not have any tags', 'dont have any tags',
    'do not have a tag', 'have no tags', 'without an etiqueta',
    'sin asignar', 'sin responsable', 'sin etiquetas', 'sin etiqueta', 'no tienen etiquetas', 'nadie'
];

export const NONE_PROJECT_PHRASES = [
    'no project', 'without a project', 'projectless', 'do not have a project', 'dont have a project',
    'with no project', 'have no project', 'sin proyecto', 'no tienen proyecto'
];

/**
 * "tasks with the same title" — the duplicate-title filter.
 *
 * A real filter dimension (filterState.duplicateTitles), not an unsupported term:
 * it selects every task whose title is shared by at least one other task. Matched
 * as a whole phrase so an ordinary word like "duplicate" never triggers it on its
 * own without the surrounding intent.
 */
export const DUPLICATE_TITLE_PHRASES = [
    'same title', 'same titles', 'same name', 'the same title', 'identical title', 'identical titles',
    'duplicate title', 'duplicate titles', 'duplicated title', 'duplicated titles',
    'repeated title', 'repeated titles', 'duplicate task', 'duplicate tasks', 'duplicated task',
    'duplicated tasks', 'duplicate tasks by title', 'duplicates',
    // Spanish. Argentine users say "vencimiento"/"repes" and, like all Spanish
    // speakers, put the adjective AFTER the noun ("titulos iguales", not "igual
    // titulo"), so both orders are listed. Feminine forms agree with "tarea(s)";
    // the masculine "duplicados"/"repetidos" agree with "titulos"/"nombres".
    'mismo titulo', 'mismos titulos', 'el mismo titulo', 'igual titulo', 'titulos iguales', 'titulo igual',
    'mismo nombre', 'mismos nombres', 'el mismo nombre', 'igual nombre', 'nombres iguales', 'nombre igual',
    'titulo repetido', 'titulos repetidos', 'titulo duplicado', 'titulos duplicados',
    'nombre repetido', 'nombres repetidos', 'nombre duplicado', 'nombres duplicados',
    'tarea duplicada', 'tareas duplicadas', 'tarea repetida', 'tareas repetidas',
    'tarea igual', 'tareas iguales', 'tareas repes', 'tarea repe',
    'duplicadas', 'duplicados', 'repetidas', 'repetidos', 'repes'
];

/** A negation immediately before a matched span flips that dimension to exclude. */
export const NEGATION_TOKENS = [
    'not', 'no', 'except', 'excluding', 'without', 'other than', 'apart from',
    'sin', 'excepto', 'salvo', 'menos'
];

/** Forces filterState.tagAndMode — the task must carry every tag, not any. */
export const TAG_AND_TOKENS = ['both', 'all of', 'todas las', 'ambas', 'ambos'];

/** After one of these, the next unmatched word is looked up as a tag or project. */
export const TAG_PREPOSITIONS = [
    'assigned to', 'tagged', 'tag', 'label', 'labelled', 'labeled', 'owned by', 'for', 'by',
    'asignada a', 'asignadas a', 'etiqueta', 'etiquetas', 'etiquetada', 'etiquetadas', 'de', 'para', 'con'
];

export const PROJECT_PREPOSITIONS = [
    'in project', 'from project', 'project', 'in', 'from',
    'del proyecto', 'en el proyecto', 'proyecto', 'en'
];

/**
 * Cues that state which KIND of thing a name is. When a name exists as both a tag
 * and a project — which happens — these decide, instead of the parser guessing.
 */
export const TAG_CUES = [
    'tag', 'tags', 'tagged', 'label', 'labelled', 'labeled', 'assigned to', 'assignee',
    'etiqueta', 'etiquetas', 'etiquetada', 'etiquetadas', 'asignada'
];

export const PROJECT_CUES = [
    'project', 'projects', 'in', 'from', 'within',
    'proyecto', 'proyectos', 'del', 'en'
];

/**
 * Words too ordinary to be recognised as a tag or project name on their own.
 *
 * Real Nautilus data contains tags like "find", "tag", "go", "do", "test" and "add".
 * Without this guard, "Can you FIND me tasks with TAG Palythoa" resolves "find" and
 * "tag" as tag filters and returns nothing — technically obeying the user's data and
 * completely betraying their intent.
 *
 * A name on this list is still fully usable: it just has to be pointed at
 * explicitly ("tagged test"), which is exactly how a human would disambiguate it.
 * Distinctive names ("Palythoa", "photogrammetry") continue to match freely.
 */
export const COMMON_WORDS = new Set([
    'find', 'search', 'tag', 'tags', 'task', 'tasks', 'project', 'projects', 'add',
    'new', 'old', 'go', 'do', 'does', 'did', 'make', 'help', 'need', 'want', 'like',
    'more', 'less', 'most', 'least', 'never', 'always', 'nice', 'good', 'bad', 'try',
    'test', 'tests', 'testing', 'example', 'sample', 'demo', 'first', 'second', 'third',
    'fourth', 'fifth', 'last', 'next', 'super', 'duper', 'very', 'much', 'many', 'some',
    'thing', 'things', 'stuff', 'item', 'items', 'will', 'would', 'could', 'should',
    'and', 'but', 'yes', 'no', 'ok', 'okay', 'data', 'info', 'work', 'day', 'week',
    'buscar', 'buscando', 'nuevo', 'nueva', 'viejo', 'hacer', 'ayuda', 'cosa', 'cosas',
    'prueba', 'pruebas', 'ejemplo', 'primero', 'segundo', 'tercero', 'ultimo', 'trabajo'
]);

export const SEARCH_PREPOSITIONS = [
    'containing', 'contains', 'mentioning', 'mentions', 'about', 'related to',
    'search for', 'matching', 'with the word', 'que contengan', 'que mencionen', 'sobre'
];

/** Follow-up intent. See mergeKaiQuery in utils/kai-query-shared.js. */
export const MODE_TOKENS = {
    'only': 'add', 'just': 'add', 'and also': 'add', 'also': 'add', 'narrow': 'add',
    'add': 'add', 'include': 'add', 'solo': 'add', 'ademas': 'add', 'incluye': 'add',
    'remove': 'remove', 'drop': 'remove', 'get rid of': 'remove', 'quita': 'remove', 'elimina': 'remove',
    'clear': 'clear', 'clear all': 'clear', 'reset': 'clear', 'start again': 'clear',
    'start over': 'clear', 'limpia': 'clear', 'reinicia': 'clear', 'empieza de nuevo': 'clear'
};

/**
 * Phrases Nautilus genuinely cannot express. Matching one produces an honest
 * "Nautilus doesn't track that" note rather than a fabricated filter.
 *
 * `reason` is an i18n key suffix under askKai.find.unsupported.*
 */
export const UNSUPPORTED_TERMS = [
    { phrases: ['estimated effort', 'estimate', 'effort', 'story points', 'points', 'esfuerzo'], reason: 'effort' },
    { phrases: ['milestone', 'sprint', 'epic', 'hito'], reason: 'milestone' },
    { phrases: ['workspace', 'espacio de trabajo'], reason: 'workspace' },
    { phrases: ['subtask', 'subtasks', 'parent task', 'subtarea', 'subtareas'], reason: 'subtask' },
    { phrases: ['category', 'categoria'], reason: 'category' },
    { phrases: ['no priority', 'without priority', 'sin prioridad'], reason: 'noPriority' },
    // Nautilus has no assignee field at all. A bare "assignee"/"owner" with no
    // name attached cannot become a tag filter, so say so plainly.
    { phrases: ['assignee', 'assignees', 'owner', 'owners', 'responsable'], reason: 'assignee' }
];

/**
 * "the five most…", "top 10", "first 3", "las últimas 5 tareas" — a result limit.
 *
 * Supported (paired with a recency ordering, see RECENCY_* below): the parser sets
 * filters.limit and the preview slices to it. The negative lookahead keeps "last 7
 * DAYS" / "próximas 2 semanas" out — those are time periods handled elsewhere.
 * Matched against normalized (lower-cased, accent-stripped) text, so the Spanish
 * arm lists un-accented forms ("ultimas", "proximos").
 */
export const LIMIT_PATTERN = /\b(?:the\s+)?(?:top|first|last|next)\s+(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten)(?!\s+(?:days?|weeks?|months?|hours?|minutes?|dias?|semanas?|meses|horas?))\b|\b(?:the\s+)?(?:\d+|five|ten)\s+most\b|\b(?:las|los|ultimas|ultimos|primeras|primeros|proximas|proximos)\s+(?:\d+|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)(?!\s+(?:dias?|semanas?|meses|horas?|minutos?))\b/i;

/** Spanish number words the limit pattern may capture, so the count survives. */
export const SPANISH_NUMBER_WORDS = {
    uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5,
    seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10
};

/**
 * Recency ranking cues, keyed to a KAI_SORTS value. "the 5 most recently modified",
 * "las últimas tareas creadas" — an ordering that pairs with LIMIT_PATTERN. A bare
 * "últimas/latest" with no verb defaults to recent-updated (last touched), matching
 * the updated-window machinery. This is NOT a general sort: arbitrary column sorts
 * ("sort by due date") stay unsupported via SORT_PATTERN.
 */
export const RECENCY_UPDATED_CUES = [
    'recently updated', 'recently modified', 'recently changed', 'most recently updated',
    'most recently modified', 'last updated', 'last modified', 'latest updated',
    'actualizadas', 'modificadas', 'cambiadas', 'editadas',
    'recientemente actualizadas', 'recientemente modificadas'
];
export const RECENCY_CREATED_CUES = [
    'recently created', 'recently added', 'most recently created', 'newest', 'last created',
    'creadas', 'nuevas', 'agregadas', 'anadidas', 'recientemente creadas'
];
/** Generic "latest/most recent/últimas" with no verb → defaults to recent-updated. */
export const RECENCY_GENERIC_CUES = [
    'most recent', 'latest', 'recientes', 'mas recientes', 'ultimas', 'ultimos'
];

/**
 * Arbitrary column sort and grouping are still unsupported — the list view sorts
 * by column click only, and there is no group-by dimension. (Recency ranking for
 * "last N …" is handled above, before this pattern, so it is not caught here.)
 */
export const SORT_PATTERN = /\b(?:sort|sorted|order|ordered|ordena|ordenadas)\b[\s\w]*\bby\b|\bfirst\b.*\bby\b|\bnewest to oldest\b|\boldest\b/i;
export const GROUP_PATTERN = /\bgroup(?:ed)?\b[\s\w]*\bby\b|\bagrupa(?:das)?\b[\s\w]*\bpor\b/i;

/**
 * Words that carry no filtering meaning on their own — excluded from the
 * coverage score and never treated as an unresolved name.
 *
 * The entity nouns (task/project) live here too: the entity is extracted
 * separately by resolveEntity(), so leaving them as "content" would permanently
 * cap confidence below the escalation threshold and send every query to the LLM.
 *
 * Prepositions are stopwords for *scoring* but are still read positionally by
 * resolveLeftovers(), so "from Atlantis" still knows Atlantis should be a project.
 */
export const STOPWORDS = new Set([
    'kai', 'show', 'me', 'my', 'all', 'the', 'a', 'an', 'find', 'list', 'get', 'give',
    'please', 'display', 'see', 'view', 'want', 'need', 'that', 'are', 'is', 'which',
    'have', 'has', 'with', 'and', 'or', 'of', 'to', 'still', 'them', 'those', 'ones',
    'one', 'it', 'do', 'does', 'can', 'you', 'i', 'assigned', 'tagged', 'only', 'just',
    'task', 'tasks', 'project', 'projects', 'priority', 'priorities', 'status',
    'due', 'date', 'dates', 'in', 'from', 'by', 'for', 'on', 'at', 'any', 'some',
    // Negation and tag-AND markers: read positionally by hasNegationBefore() /
    // hasTagAndBefore(), so they must never be scored as content or resolved as names.
    'not', 'except', 'excluding', 'without', 'other', 'than', 'apart', 'both',
    'excepto', 'salvo', 'menos', 'ambas', 'ambos', 'sin',
    // Filler that can sit between a negation and the word it negates
    // ("not YET done", "have not BEEN done"). Without these, hasNegationBefore()
    // stops walking back and the negation is silently lost.
    'yet', 'been', 'being', 'aun', 'todavia',
    // Verbs describing an action on a task, never the name of one. Without these,
    // "tasks that have been MODIFIED in the past 4 days" hunts for a tag called
    // "modified" and reports it missing.
    'modified', 'updated', 'changed', 'edited', 'touched', 'created',
    'modificadas', 'actualizadas', 'cambiadas', 'editadas',
    'muestra', 'muestrame', 'ensename', 'dame', 'todas', 'todos', 'las', 'los', 'el', 'la',
    'un', 'una', 'que', 'estan', 'esta', 'esten', 'este', 'son', 'sea', 'sean', 'y', 'o', 'de', 'del', 'mis', 'mi',
    'tarea', 'tareas', 'proyecto', 'proyectos', 'prioridad', 'estado', 'solo', 'con',
    // Argentine voseo query verbs ("mostrame", "decime", "contame") and their
    // infinitive+clitic forms. QUERY_INTENT_PATTERN already routes them, but without
    // them here they leak as leftover "content", dragging confidence below the
    // escalation threshold on plainly-parseable requests.
    'mostrame', 'mostrar', 'mostrarme', 'decime', 'decirme', 'contame', 'contar',
    'listame', 'listar', 'buscame', 'buscar', 'encontrar', 'encontrame', 'ensenar', 'ver',
    'dime', 'quiero', 'necesito',
    // More Argentine voseo imperatives: "fijate si hay…", "traeme…", "pasame…",
    // "mandame…", "che" as a conversational opener.
    'fijate', 'fijar', 'traeme', 'traer', 'pasame', 'pasar', 'mandame', 'mandar', 'che'
]);
